/**
 * Build an SVG line path from y-values using Catmull-Rom → cubic Bézier.
 * Produces a smooth curve that still passes through every data point
 * (unlike a pure spline, which can overshoot). Returns the stroke path
 * as `line` and the closed area path (down to y=h-pad, back to x=0)
 * as `area`, so the caller can fill one and stroke the other.
 *
 * Shared between Heartbeat and the Wall row sparklines.
 */
export interface SmoothPath { line: string; area: string }

export function smoothPath(
  values: number[],
  opts: { width: number; height: number; pad?: number },
): SmoothPath {
  const { width: w, height: h } = opts;
  const pad = opts.pad ?? 2;
  if (values.length === 0) return { line: '', area: '' };

  const max = Math.max(1, ...values);
  const innerH = h - pad * 2;
  const step = values.length > 1 ? w / (values.length - 1) : w;
  const pts: ReadonlyArray<readonly [number, number]> = values.map((v, i) => [
    i * step,
    pad + innerH - (v / max) * innerH,
  ]);

  let line = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6;
    const c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6;
    const c2y = p2[1] - (p3[1] - p1[1]) / 6;
    line += ` C ${c1x},${c1y} ${c2x},${c2y} ${p2[0]},${p2[1]}`;
  }
  const area = `${line} L ${w},${h - pad} L 0,${h - pad} Z`;
  return { line, area };
}
